import { clsx } from "clsx";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	DEFAULT_REACTION,
	REACTION_OPTIONS,
	type ReactionType,
	reactionOption,
} from "@/features/feed/reactions.ts";
import type { Post } from "@/features/feed/types";

const HOVER_OPEN_DELAY = 300;
const HOVER_CLOSE_DELAY = 300;
const LONG_PRESS_DELAY = 500;

function hasHover() {
	return (
		typeof window !== "undefined" &&
		typeof window.matchMedia === "function" &&
		window.matchMedia("(hover: hover)").matches
	);
}

interface ReactionButtonProps {
	post: Post;
	disabled: boolean;
	onReact: (type: ReactionType) => void;
}

export function ReactionButton({
	post,
	disabled,
	onReact,
}: ReactionButtonProps) {
	const [open, setOpen] = useState(false);
	const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const suppressClick = useRef(false);

	const clearTimers = useCallback(() => {
		for (const timer of [openTimer, closeTimer, longPressTimer]) {
			if (timer.current) {
				clearTimeout(timer.current);
				timer.current = null;
			}
		}
	}, []);

	useEffect(() => clearTimers, [clearTimers]);

	useEffect(() => {
		if (disabled) {
			clearTimers();
			setOpen(false);
		}
	}, [disabled, clearTimers]);

	const cancelClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);

	const scheduleClose = useCallback(() => {
		if (!hasHover()) return;
		cancelClose();
		closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY);
	}, [cancelClose]);

	function handlePointerEnter() {
		if (disabled || !hasHover()) return;
		cancelClose();
		if (openTimer.current) clearTimeout(openTimer.current);
		openTimer.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY);
	}

	function handlePointerLeave() {
		if (openTimer.current) {
			clearTimeout(openTimer.current);
			openTimer.current = null;
		}
		scheduleClose();
	}

	function handlePointerDown() {
		if (disabled || hasHover()) return;
		suppressClick.current = false;
		longPressTimer.current = setTimeout(() => {
			suppressClick.current = true;
			setOpen(true);
		}, LONG_PRESS_DELAY);
	}

	function handlePointerUp() {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	}

	function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
		event.preventDefault();
		if (suppressClick.current) {
			suppressClick.current = false;
			return;
		}
		if (disabled) return;
		clearTimers();
		setOpen(false);
		onReact(post.reactions.type ?? DEFAULT_REACTION);
	}

	function handleSelect(type: ReactionType) {
		clearTimers();
		setOpen(false);
		onReact(type);
	}

	const { count, reacted, type, types } = post.reactions;
	const face = reactionOption(reacted && type ? type : DEFAULT_REACTION);

	return (
		<Popover open={open && !disabled} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className={clsx("post-card__action", reacted && "is-reacted")}
					disabled={disabled}
					onPointerEnter={handlePointerEnter}
					onPointerLeave={handlePointerLeave}
					onPointerDown={handlePointerDown}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					onClick={handleClick}
				>
					<span aria-hidden="true">{face.emoji}</span> {face.label}
					{count > 0 && (
						<>
							{types.length > 0 && (
								<span
									className="post-card__reaction-summary"
									aria-hidden="true"
								>
									{types.map((item) => (
										<span key={item}>{reactionOption(item).emoji}</span>
									))}
								</span>
							)}
							<span className="post-card__action-count">{count}</span>
						</>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				side="top"
				align="start"
				className="post-card__reaction-picker"
				onPointerEnter={cancelClose}
				onPointerLeave={scheduleClose}
			>
				{REACTION_OPTIONS.map((option) => (
					<button
						key={option.type}
						type="button"
						aria-label={option.label}
						onClick={() => handleSelect(option.type)}
					>
						<span aria-hidden="true">{option.emoji}</span>
					</button>
				))}
			</PopoverContent>
		</Popover>
	);
}
